// src/App.js
import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useState, lazy, Suspense } from "react";

// ── Loaded instantly (needed on every page / first paint) ──
import HeroSection from "./pages/Home/HeroSection"; // homepage stays instant
import LoginModal from "./components/LoginModal/LoginModal";
import LayoutWithHeader from "./layouts/LayoutWithHeader";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteChangeTracker from "./components/RouteChangeTracker";
import PWAInstallPrompt from "./components/PWAInstallPrompt/PWAInstallPrompt";

// ── Loaded only when the visitor opens that page (code splitting) ──
// Each of these becomes its own small file, downloaded on demand.
const About = lazy(() => import("./pages/About/About"));
const Careers = lazy(() => import("./pages/Careers/Careers"));
const Business = lazy(() => import("./pages/Business/Business"));
const Company = lazy(() => import("./pages/Companies/Companies"));
const Contact = lazy(() => import("./pages/Contact/Contact"));
const Terms = lazy(() => import("./components/Footer/tc"));
const Policy = lazy(() => import("./components/Footer/pp"));
const DataProtection = lazy(() => import("./components/Footer/dp"));
const Main = lazy(() => import("./Programming/Html and css/HLayout"));
const Main5 = lazy(() => import("./Programming/Crypto/CtLayout"));
const Main2 = lazy(() => import("./Programming/Python/PLayout"));
const Main3 = lazy(() => import("./Programming/Java/JLayout"));
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const DynamicEditor = lazy(() => import("./Programming/Html and css/DynamicEditor"));
const PtCourse = lazy(() => import("./Programming/Pytorch/PtLayout"));
const PtDynamicEditor = lazy(() => import("./Programming/Pytorch/PtDynamicEditor"));
const MmCourse = lazy(() => import("./Programming/Mllm3d/MmLayout"));
const MmDynamicEditor = lazy(() => import("./Programming/Mllm3d/MmDynamicEditor"));
const DpCourse = lazy(() => import("./Programming/Deploy/DpLayout"));
const DpDynamicEditor = lazy(() => import("./Programming/Deploy/DpDynamicEditor"));
const MlCourse = lazy(() => import("./Programming/Ml/MlLayout"));
const MlDynamicEditor = lazy(() => import("./Programming/Ml/MlDynamicEditor"));
const MlopsCourse = lazy(() => import("./Programming/Mlops/MlLayout"));
const MlopsDynamicEditor = lazy(() => import("./Programming/Mlops/MlEditor/MlDynamicEditor"));
const CsCourse = lazy(() => import("./Programming/Cs/CsLayout"));
const CsDynamicEditor = lazy(() => import("./Programming/Cs/CsDynamicEditor"));
const NeCourse = lazy(() => import("./Programming/Network/NeLayout"));
const NeDynamicEditor = lazy(() => import("./Programming/Network/NeDynamicEditor"));
const AeCourse = lazy(() => import("./Programming/AiEng/AeLayout"));
const AeDynamicEditor = lazy(() => import("./Programming/AiEng/AeDynamicEditor"));
const FsCourse = lazy(() => import("./Programming/Fullstack/FsLayout"));
const GdCourse = lazy(() => import("./Programming/GameDev/GdLayout"));
const GdDynamicEditor = lazy(() => import("./Programming/GameDev/GdDynamicEditor"));
const UxCourse = lazy(() => import("./Programming/UxDesign/UxLayout"));
const UxDynamicEditor = lazy(() => import("./Programming/UxDesign/UxDynamicEditor"));
const CvCourse = lazy(() => import("./Programming/CV/CvLayout"));
const DataCourse = lazy(() => import("./Programming/Data/DataLayout"));
const DeCourse = lazy(() => import("./Programming/DataEng/DeLayout"));
const DoCourse = lazy(() => import("./Programming/DevOps/DoLayout"));
const FdeCourse = lazy(() => import("./Programming/Fde/FdeLayout"));
const GitCourse = lazy(() => import("./Programming/Git/GitLayout"));
const IeltsCourse = lazy(() => import("./Programming/IELTS/IeltsLayout"));
const SqlCourse = lazy(() => import("./Programming/Sql/SqlLayout"));
const VlaCourse = lazy(() => import("./Programming/VLA/VLALayout"));
const HpCourse = lazy(() => import("./Programming/HousePrice/HpLayout"));
const AllCourses = lazy(() => import("./pages/AllCourses/AllCourses"));



// Small centered spinner shown for a moment while a page's code downloads.
// Self-contained (style included) so it needs no extra CSS file.
const PageLoader = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60vh",
    }}
  >
    <style>{`@keyframes inelf-spin { to { transform: rotate(360deg); } }`}</style>
    <div
      style={{
        width: "42px",
        height: "42px",
        border: "4px solid rgba(71, 165, 197, 0.2)",
        borderTopColor: "#47a5c5",
        borderRadius: "50%",
        animation: "inelf-spin 0.8s linear infinite",
      }}
      aria-label="Loading"
    />
  </div>
);

function App() {
  const [isModalOpen, setModalOpen] = useState(false);
  // State to track if the PWA install prompt is currently active
  // This state is managed by the PWAInstallPrompt component.
  const [isPwaPromptActive, setIsPwaPromptActive] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleLoginSuccess = () => {
    setModalOpen(false);
  };

  return (
    <AuthProvider>
      <Router>
        <RouteChangeTracker />

        {/* This component will handle showing the PWA install prompt.
          It is placed here at the top level to ensure it can overlay any content.
          It will also manage the 'isPwaPromptActive' state.
        */}
        <PWAInstallPrompt onPromptVisibilityChange={setIsPwaPromptActive} />

        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<LayoutWithHeader openModal={openModal} />}>
              <Route path="/" element={<HeroSection />} />
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/Companies" element={<Company />} />
              <Route path="/business" element={<Business />} />
              <Route path="/Contact" element={<Contact />} />
              <Route path="/terms-conditions" element={<Terms />} />
              <Route path="/privacy-policy" element={<Policy />} />
              <Route path="/DataProtection" element={<DataProtection />} />
              <Route path="/Courses/HTML" element={<Main />} />
              <Route path="/Courses/crypto" element={<Main5 />} />
              <Route path="/Courses/python" element={<Main2 />} />
              <Route path="/Courses/java" element={<Main3 />} />
              <Route path="/Courses/pytorch" element={<PtCourse />} />
              <Route path="/Courses/mllm3d" element={<MmCourse />} />
            <Route path="/Courses/deployment" element={<DpCourse />} />
              <Route path="/Courses/machinelearning" element={<MlCourse />} />
              <Route path="/Courses/mlops" element={<MlopsCourse />} />
              <Route path="/Courses/cybersecurity" element={<CsCourse />} />
              <Route path="/Courses/networking" element={<NeCourse />} />
              <Route path="/Courses/aiengineering" element={<AeCourse />} />
              <Route path="/Courses/fullstack" element={<FsCourse />} />
              <Route path="/Courses/gamedev" element={<GdCourse />} />
              <Route path="/Courses/ux-design" element={<UxCourse />} />
              <Route path="/Courses/computer-vision" element={<CvCourse />} />
              <Route path="/Courses/data-analytics" element={<DataCourse />} />
              <Route path="/Courses/data-engineering" element={<DeCourse />} />
              <Route path="/Courses/devops" element={<DoCourse />} />
              <Route path="/Courses/fde" element={<FdeCourse />} />
              <Route path="/Courses/git" element={<GitCourse />} />
              <Route path="/Courses/ielts" element={<IeltsCourse />} />
              <Route path="/Courses/sql" element={<SqlCourse />} />
              <Route path="/Courses/vla" element={<VlaCourse />} />
              <Route path="/Courses/house-price" element={<HpCourse />} />
              
              <Route path="/courses" element={<AllCourses />} />
              <Route path="/dashboard" element={<Dashboard />} />
            </Route>

            <Route
              path="/HTML/Hcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <DynamicEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/Pytorch/Ptcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <PtDynamicEditor />
                </ProtectedRoute>
              }
            />

            
            <Route
              path="/Ux/Uxcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <UxDynamicEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/Mllm3d/Mmcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <MmDynamicEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Deploy/Dpcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <DpDynamicEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Ml/Mlcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <MlDynamicEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Mlops/Mlopscode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <MlopsDynamicEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Cyber/Cscode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <CsDynamicEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/Deploy/Netcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <NeDynamicEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/AiEng/Aecode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <AeDynamicEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/GameDev/Gdcode/:id"
              element={
                <ProtectedRoute openModal={openModal}>
                  <GdDynamicEditor />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<h1>Not found</h1>} />
          </Routes>
        </Suspense>

        <LoginModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onLoginSuccess={handleLoginSuccess}
        />
      </Router>
    </AuthProvider>
  );
}

export default App;